import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getSubjectNoteList(
    $filter: subjectNoteFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getSubjectNoteList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        noteBy {
          id
          name
          photo
        }
        datetime
        type
        content
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.SubjectNoteListState = {
  result: null,
  error: null,
  loading: false
};

export const getSubjectNoteListRequest = createAsyncThunk<
  TYPES.SubjectNoteList,
  {
    filter?: TYPES.SubjectNoteFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'subject_note/get_subject_note_list',
  async (
  params: {
    filter?: TYPES.SubjectNoteFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getSubjectNoteList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getSubjectNoteListSlice = createSlice({
  name: 'get_subject_note_list',
  initialState: initialState,
  reducers: {
    getSubjectNoteListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getSubjectNoteListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getSubjectNoteListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getSubjectNoteListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getSubjectNoteListClear } =
getSubjectNoteListSlice.actions;

export const getSubjectNoteList = createSelector(
  (state: RootState) => state.subjectNote.getSubjectNoteList.result,
  (state: RootState) => state.subjectNote.getSubjectNoteList.error,
  (state: RootState) => state.subjectNote.getSubjectNoteList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getSubjectNoteListSlice.reducer;